import React, { useState } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  Eye, 
  Edit, 
  Trash2, 
  Package,
  MapPin,
  Phone,
  User,
  Calendar,
  Printer,
  Send,
  SortDesc,
  Settings2,
  Grid,
  List,
  CalendarDays,
  CheckSquare,
  Square,
  Building2,
  ChevronLeft,
  ChevronRight,
  MapPin as TrackingIcon,
  Copy,
  ArrowUpDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import OrderForm from '@/components/orders/OrderForm';
import OrderDetails from '@/components/orders/OrderDetails';
import ThirdPartyManager from '@/components/orders/ThirdPartyManager';
import { mockOrders } from '@/api/mockData';
import { useToast } from '@/hooks/use-toast';

const Orders = () => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [showPrintLabel, setShowPrintLabel] = useState(false);
  const [printOrder, setPrintOrder] = useState(null);
  
  // Debug log to verify state is declared
  console.log('State declared:', { showPrintLabel, setShowPrintLabel });
  const [showTrackingDialog, setShowTrackingDialog] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterHub, setFilterHub] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterCreator, setFilterCreator] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [activeTab, setActiveTab] = useState('orders');
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); // Cho phép thay đổi số lượng item/trang
  const { toast } = useToast();

  // Mock hubs data
  const mockHubs = [
    { id: '1', name: 'Bưu cục Trung tâm' },
    { id: '2', name: 'Bưu cục Đông' },
    { id: '3', name: 'Bưu cục Tây' },
    { id: '4', name: 'Bưu cục Nam' },
    { id: '5', name: 'Bưu cục Bắc' },
  ];

  // Generate more mock orders for pagination demo
  const generateMoreOrders = () => {
    const moreOrders = [];
    for (let i = 0; i < 50; i++) {
      const baseOrder = mockOrders[i % mockOrders.length];
      moreOrders.push({
        ...baseOrder,
        id: `DH${String(1000 + i).padStart(3, '0')}`,
        timestamp: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
        hub: mockHubs[Math.floor(Math.random() * mockHubs.length)].name
      });
    }
    return [...mockOrders, ...moreOrders];
  };

  const allOrders = generateMoreOrders();

  const filteredOrders = allOrders.filter(order => {
    const matchesSearch = order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.sender.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.recipient.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || order.status === filterStatus;
    const matchesHub = filterHub === 'all' || (order as any).hub === filterHub;
    const matchesCreator = filterCreator === 'all' || (order as any).createdBy === filterCreator;
    
    // Date filtering
    let matchesDate = true;
    if (filterDateFrom || filterDateTo) {
      const orderDate = new Date(order.timestamp);
      if (filterDateFrom) {
        matchesDate = matchesDate && orderDate >= new Date(filterDateFrom);
      }
      if (filterDateTo) {
        matchesDate = matchesDate && orderDate <= new Date(filterDateTo);
      }
    }
    
    return matchesSearch && matchesStatus && matchesHub && matchesCreator && matchesDate;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      case 'oldest':
        return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
      case 'code':
        return a.id.localeCompare(b.id);
      case 'status':
        return a.status.localeCompare(b.status);
      default:
        return 0;
    }
  });

  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'Hoàn thành':
      case 'Đã giao':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'Đang giao':
      case 'Đang luân chuyển':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'Chờ xử lý':
      case 'Mới tạo':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'Bị hủy':
      case 'Giao thất bại':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: 
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const handleEditOrder = (order) => {
    setEditingOrder(order);
    setShowEditDialog(true);
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setShowDetailsDialog(true);
  };

  const handleOrderSelect = (orderId: string, checked: boolean) => {
    if (checked) {
      setSelectedOrders([...selectedOrders, orderId]);
    } else {
      setSelectedOrders(selectedOrders.filter(id => id !== orderId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedOrders(paginatedOrders.map(order => order.id));
    } else {
      setSelectedOrders([]);
    }
  };

  const handleBulkAction = (action: string) => {
    toast({
      title: "Thao tác hàng loạt",
      description: `Đã ${action} ${selectedOrders.length} đơn hàng`,
    });
    setSelectedOrders([]);
  };

  const handlePrintLabel = (order) => {
    setPrintOrder(order);
    setShowPrintLabel(true);
  };

  const handleTrackOrder = (order) => {
    window.open(`/tracking/${order.id}`, '_blank');
  };

  const handleDeleteOrder = (order) => {
    toast({
      title: "Xóa đơn hàng",
      description: `Đã xóa đơn hàng ${order.id}`,
    });
  };

  const handlePhoneCall = (phone: string, name: string) => {
    toast({
      title: "Mở cuộc gọi",
      description: `Đang gọi ${name} - ${phone}`,
    });
  };

  if (activeTab === '3pl') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setActiveTab('orders')}
            >
              ← Quay lại
            </Button>
            <h1 className="text-3xl font-bold text-foreground">Quản lý 3PL</h1>
          </div>
        </div>
        <ThirdPartyManager />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-foreground">Quản lý đơn hàng</h1>
        <div className="flex gap-2">
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90">
                <Plus className="h-4 w-4 mr-2" />
                Tạo đơn hàng
              </Button>
            </DialogTrigger>
            <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 p-0 overflow-hidden flex flex-col">
              <DialogHeader className="px-6 py-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                <DialogTitle className="text-xl">Tạo đơn hàng mới</DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto px-6 py-4">
                <OrderForm onClose={() => setShowCreateDialog(false)} />
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
            <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 p-0 overflow-hidden flex flex-col">
              <DialogHeader className="px-6 py-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                <DialogTitle className="text-xl">Chỉnh sửa đơn hàng {editingOrder?.id}</DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto px-6 py-4">
                <OrderForm 
                  order={editingOrder}
                  onClose={() => {
                    setShowEditDialog(false);
                    setEditingOrder(null);
                  }} 
                />
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="orders" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Danh sách đơn hàng
          </TabsTrigger>
          <TabsTrigger value="3pl" className="flex items-center gap-2">
            <Send className="h-4 w-4" />
            Quản lý 3PL
          </TabsTrigger>
        </TabsList>

        <TabsContent value="orders">
          <Card className="animate-fade-in">
            <CardHeader>
              <div className="flex items-center justify-between mb-4">
                <CardTitle className="flex items-center">
                  <Package className="h-5 w-5 mr-2 text-primary" />
                  Danh sách đơn hàng ({filteredOrders.length})
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    variant={viewMode === 'list' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('list')}
                  >
                    <List className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('grid')}
                  >
                    <Grid className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'table' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('table')}
                  >
                    <ArrowUpDown className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Filters */}
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <div className="relative">
                    <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
                    <Input
                      placeholder="Tìm kiếm đơn hàng..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-64"
                    />
                  </div>

                  <Select value={filterHub} onValueChange={setFilterHub}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Bưu cục" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tất cả bưu cục</SelectItem>
                      {mockHubs.map((hub) => (
                        <SelectItem key={hub.id} value={hub.name}>{hub.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Trạng thái" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tất cả trạng thái</SelectItem>
                      <SelectItem value="Mới tạo">Mới tạo</SelectItem>
                      <SelectItem value="Chờ xử lý">Chờ xử lý</SelectItem>
                      <SelectItem value="Đang giao">Đang giao</SelectItem>
                      <SelectItem value="Hoàn thành">Hoàn thành</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Sắp xếp" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="newest">Mới nhất</SelectItem>
                      <SelectItem value="oldest">Cũ nhất</SelectItem>
                      <SelectItem value="code">Mã đơn</SelectItem>
                      <SelectItem value="status">Trạng thái</SelectItem>
                    </SelectContent>
                  </Select>

                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Filter className="h-4 w-4 mr-2" />
                        Lọc nâng cao
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80">
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Từ ngày</Label>
                          <Input
                            type="date"
                            value={filterDateFrom}
                            onChange={(e) => setFilterDateFrom(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Đến ngày</Label>
                          <Input
                            type="date"
                            value={filterDateTo}
                            onChange={(e) => setFilterDateTo(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Người tạo</Label>
                          <Select value={filterCreator} onValueChange={setFilterCreator}>
                            <SelectTrigger>
                              <SelectValue placeholder="Chọn người tạo" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Tất cả</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="staff">Nhân viên</SelectItem>
                              <SelectItem value="customer">Khách hàng</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setFilterDateFrom('');
                            setFilterDateTo('');
                            setFilterCreator('all');
                          }}
                          className="w-full"
                        >
                          Xóa bộ lọc
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Bulk actions */}
                {selectedOrders.length > 0 && (
                  <div className="flex items-center gap-2 p-3 bg-primary/5 rounded-lg border">
                    <span className="text-sm font-medium">
                      Đã chọn {selectedOrders.length} đơn hàng
                    </span>
                    <Button size="sm" variant="outline" onClick={() => handleBulkAction('xuất')}>
                      Xuất Excel
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleBulkAction('in tem')}>
                      In tem hàng loạt
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleBulkAction('gửi 3PL')}>
                      Gửi 3PL
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setSelectedOrders([])}>
                      Bỏ chọn
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Select all */}
                <div className="flex items-center gap-3 pb-2 border-b">
                  <Checkbox
                    checked={paginatedOrders.length > 0 && selectedOrders.length === paginatedOrders.length}
                    onCheckedChange={handleSelectAll}
                  />
                  <span className="text-sm font-medium">Chọn tất cả</span>
                </div>

                {viewMode === 'list' ? (
                  // List view
                  <div className="space-y-3">
                    {paginatedOrders.map((order) => (
                      <div
                        key={order.id}
                        className="p-4 border rounded-lg hover:shadow-md transition-all duration-200"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <Checkbox
                              checked={selectedOrders.includes(order.id)}
                              onCheckedChange={(checked) => handleOrderSelect(order.id, checked as boolean)}
                            />
                            <div className="bg-primary/10 p-2 rounded-lg">
                              <Package className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-lg">{order.id}</h3>
                                {order.is3PL && (
                                  <Badge variant="outline" className="text-xs">3PL</Badge>
                                )}
                                {order.isDraft && (
                                  <Badge variant="secondary" className="text-xs">Nháp</Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {new Date(order.timestamp).toLocaleString('vi-VN')} • {(order as any).hub || 'Bưu cục Trung tâm'}
                              </p>
                            </div>
                          </div>
                          <Badge className={getStatusColor(order.status)}>
                            {order.status}
                          </Badge>
                        </div>
                        
                        {/* COD info moved to header */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{order.id}</span>
                            <Badge className={`${getStatusColor(order.status)} px-2 py-1`}>
                              {order.status}
                            </Badge>
                          </div>
                          <div className="text-right">
                            <span className="font-bold text-primary text-lg">
                              {order.codAmount.toLocaleString()}đ COD
                            </span>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div className="space-y-2">
                            <div className="flex items-center">
                              <User className="h-4 w-4 mr-2 text-muted-foreground" />
                              <a 
                                href={`tel:${order.senderPhone}`}
                                className="cursor-pointer hover:text-primary text-blue-600 hover:underline"
                              >
                                {order.sender}
                              </a>
                            </div>
                            <div className="flex items-center">
                              <Phone className="h-4 w-4 mr-2 text-muted-foreground" />
                              <span className="text-sm text-muted-foreground">
                                {order.senderPhone}
                              </span>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center">
                              <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
                              <a 
                                href={`tel:${order.recipientPhone}`}
                                className="cursor-pointer hover:text-primary text-blue-600 hover:underline"
                              >
                                {order.recipient}
                              </a>
                            </div>
                            <div className="flex items-center">
                              <Phone className="h-4 w-4 mr-2 text-muted-foreground" />
                              <span className="text-sm text-muted-foreground">
                                {order.recipientPhone} - {order.province}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between mt-3 pt-3 border-t">
                          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                            <span>Dịch vụ: {order.service}</span>
                            <span>Cân nặng: {order.weight}kg</span>
                            <span>Sản phẩm: {order.products.length}</span>
                          </div>
                          <div className="flex space-x-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleViewOrder(order)}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Xem
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleTrackOrder(order)}
                            >
                              <TrackingIcon className="h-4 w-4 mr-1" />
                              Theo dõi
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleEditOrder(order)}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Sửa
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handlePrintLabel(order)}
                            >
                              <Printer className="h-4 w-4 mr-1" />
                              In tem
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => handleDeleteOrder(order)}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Xóa
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : viewMode === 'table' ? (
                  // Table view
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left p-3">
                            <Checkbox
                              checked={paginatedOrders.length > 0 && selectedOrders.length === paginatedOrders.length}
                              onCheckedChange={handleSelectAll}
                            />
                          </th>
                          <th className="text-left p-3 font-medium">Mã đơn</th>
                          <th className="text-left p-3 font-medium">Người gửi</th>
                          <th className="text-left p-3 font-medium">Người nhận</th>
                          <th className="text-left p-3 font-medium">Địa chỉ</th>
                          <th className="text-left p-3 font-medium">Dịch vụ</th>
                          <th className="text-left p-3 font-medium">COD</th>
                          <th className="text-left p-3 font-medium">Trạng thái</th>
                          <th className="text-left p-3 font-medium">Thời gian</th>
                          <th className="text-left p-3 font-medium">Thao tác</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedOrders.map((order) => (
                          <tr key={order.id} className="border-b hover:bg-muted/30 transition-colors">
                            <td className="p-3">
                              <Checkbox
                                checked={selectedOrders.includes(order.id)}
                                onCheckedChange={(checked) => handleOrderSelect(order.id, checked as boolean)}
                              />
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{order.id}</span>
                                {order.is3PL && (
                                  <Badge variant="outline" className="text-xs">3PL</Badge>
                                )}
                                {order.isDraft && (
                                  <Badge variant="secondary" className="text-xs">Nháp</Badge>
                                )}
                              </div>
                            </td>
                            <td className="p-3">
                              <div>
                                <div className="font-medium">{order.sender}</div>
                                <div className="text-sm text-muted-foreground">{order.senderPhone}</div>
                              </div>
                            </td>
                            <td className="p-3">
                              <div>
                                <div className="font-medium">{order.recipient}</div>
                                <div className="text-sm text-muted-foreground">{order.recipientPhone}</div>
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="max-w-xs truncate" title={order.address}>
                                {order.province}
                              </div>
                            </td>
                            <td className="p-3">
                              <div>
                                <div className="text-sm">{order.service}</div>
                                <div className="text-xs text-muted-foreground">{order.weight}kg</div>
                              </div>
                            </td>
                            <td className="p-3">
                              <span className="font-medium text-primary">
                                {order.codAmount.toLocaleString()}đ
                              </span>
                            </td>
                            <td className="p-3">
                              <Badge className={getStatusColor(order.status)}>
                                {order.status}
                              </Badge>
                            </td>
                            <td className="p-3">
                              <div className="text-sm">
                                {new Date(order.timestamp).toLocaleDateString('vi-VN')}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {new Date(order.timestamp).toLocaleTimeString('vi-VN')}
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="flex space-x-1">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleViewOrder(order)}
                                  className="h-8 w-8 p-0"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleEditOrder(order)}
                                  className="h-8 w-8 p-0"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handlePrintLabel(order)}
                                  className="h-8 w-8 p-0"
                                >
                                  <Printer className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  // Grid view
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {paginatedOrders.map((order) => (
                      <Card key={order.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Checkbox
                                checked={selectedOrders.includes(order.id)}
                                onCheckedChange={(checked) => handleOrderSelect(order.id, checked as boolean)}
                              />
                              <span className="font-semibold">{order.id}</span>
                            </div>
                            <Badge className={getStatusColor(order.status)}>
                              {order.status}
                            </Badge>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div>{order.sender} → {order.recipient}</div>
                            <div className="text-muted-foreground">{order.province}</div>
                            <div className="font-medium text-primary">COD: {order.codAmount.toLocaleString()}đ</div>
                          </div>
                          <div className="flex justify-end gap-1 mt-3">
                            <Button size="sm" variant="outline" onClick={() => handleViewOrder(order)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleEditOrder(order)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handlePrintLabel(order)}>
                              <Printer className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-muted-foreground">
                        Hiển thị {Math.min((currentPage - 1) * itemsPerPage + 1, filteredOrders.length)} - {Math.min(currentPage * itemsPerPage, filteredOrders.length)} trong tổng số {filteredOrders.length} đơn hàng
                      </span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-muted-foreground">Hiển thị:</span>
                        <Select value={itemsPerPage.toString()} onValueChange={(value) => setItemsPerPage(Number(value))}>
                          <SelectTrigger className="w-20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="10">10</SelectItem>
                            <SelectItem value="20">20</SelectItem>
                            <SelectItem value="30">30</SelectItem>
                            <SelectItem value="50">50</SelectItem>
                          </SelectContent>
                        </Select>
                        <span className="text-sm text-muted-foreground">mục/trang</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Trước
                        </Button>
                        <span className="text-sm">
                          Trang {currentPage} / {totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                        >
                          Sau
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {paginatedOrders.length === 0 && (
                  <div className="text-center py-8">
                    <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-muted-foreground">Không tìm thấy đơn hàng nào</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Order Details Modal */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Chi tiết đơn hàng {selectedOrder?.id}</DialogTitle>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => handleTrackOrder(selectedOrder)}
                >
                  <TrackingIcon className="h-4 w-4 mr-2" />
                  Theo dõi đơn hàng
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => handlePrintLabel(selectedOrder)}
                >
                  <Printer className="h-4 w-4 mr-2" />
                  In tem
                </Button>
              </div>
            </div>
          </DialogHeader>
          {selectedOrder && <OrderDetails order={selectedOrder} />}
        </DialogContent>
      </Dialog>

      {/* Print Label Modal */}
      <Dialog open={showPrintDialog} onOpenChange={setShowPrintDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>In tem đơn hàng {selectedOrder?.id}</DialogTitle>
          </DialogHeader>
          <div className="border-2 border-dashed border-gray-300 p-6 bg-white text-black">
            <div className="text-center mb-4">
              <h2 className="text-xl font-bold">BẾN XE VẬN CHUYỂN</h2>
              <p className="text-sm">Hotline: 1900-xxx-xxx</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <h3 className="font-semibold mb-2">NGƯỜI GỬI:</h3>
                <p className="text-sm">{selectedOrder?.sender}</p>
                <p className="text-sm">{selectedOrder?.senderPhone}</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">NGƯỜI NHẬN:</h3>
                <p className="text-sm">{selectedOrder?.recipient}</p>
                <p className="text-sm">{selectedOrder?.recipientPhone}</p>
                <p className="text-sm">{selectedOrder?.address}</p>
              </div>
            </div>
            
            <div className="border-t pt-4">
              <div className="text-center">
                <div className="text-2xl font-bold mb-2">{selectedOrder?.id}</div>
                <div className="text-lg">COD: {selectedOrder?.codAmount.toLocaleString()}đ</div>
                <div className="text-sm">Dịch vụ: {selectedOrder?.service}</div>
                <div className="text-sm">Cân nặng: {selectedOrder?.weight}kg</div>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowPrintDialog(false)}>
              Đóng
            </Button>
            <Button onClick={() => window.print()}>
              <Printer className="h-4 w-4 mr-2" />
              In tem
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tracking Modal */}
      <Dialog open={showTrackingDialog} onOpenChange={setShowTrackingDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Theo dõi đơn hàng {selectedOrder?.id}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <h3 className="font-semibold">{selectedOrder?.id}</h3>
                <p className="text-sm text-muted-foreground">
                  {selectedOrder?.sender} → {selectedOrder?.recipient}
                </p>
              </div>
              <Badge className={getStatusColor(selectedOrder?.status)}>
                {selectedOrder?.status}
              </Badge>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 border-l-4 border-green-500">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <div className="flex-1">
                  <div className="font-medium">Đơn hàng đã được tạo</div>
                  <div className="text-sm text-muted-foreground">
                    {new Date(selectedOrder?.timestamp).toLocaleString('vi-VN')}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 border-l-4 border-blue-500">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <div className="flex-1">
                  <div className="font-medium">Đang xử lý tại bưu cục</div>
                  <div className="text-sm text-muted-foreground">
                    {new Date().toLocaleString('vi-VN')}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 border-l-4 border-gray-300">
                <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
                <div className="flex-1">
                  <div className="font-medium text-muted-foreground">Đang vận chuyển</div>
                  <div className="text-sm text-muted-foreground">Chờ cập nhật</div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Print Label Dialog */}
      <Dialog open={showPrintLabel} onOpenChange={setShowPrintLabel}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>In tem đơn hàng</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {printOrder && (
              <div className="border rounded-lg p-6 bg-white text-black" style={{ fontFamily: 'monospace' }}>
                <div className="text-center border-b pb-4 mb-4">
                  <h2 className="text-xl font-bold">HỆ THỐNG VẬN CHUYỂN XE BUS</h2>
                  <p className="text-sm">Hotline: 1900-xxxx | Website: busdelivery.vn</p>
                </div>
                
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-bold text-sm mb-2">NGƯỜI GỬI:</h3>
                    <p className="text-sm">{printOrder.sender}</p>
                    <p className="text-sm">{printOrder.senderPhone}</p>
                  </div>
                  <div>
                    <h3 className="font-bold text-sm mb-2">NGƯỜI NHẬN:</h3>
                    <p className="text-sm">{printOrder.recipient}</p>
                    <p className="text-sm">{printOrder.recipientPhone}</p>
                    <p className="text-sm">{printOrder.address}</p>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t">
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="font-bold">Mã đơn:</span>
                      <p className="text-lg font-bold">{printOrder.id}</p>
                    </div>
                    <div>
                      <span className="font-bold">Cân nặng:</span>
                      <p>{printOrder.weight} kg</p>
                    </div>
                    <div>
                      <span className="font-bold">Dịch vụ:</span>
                      <p>{printOrder.service}</p>
                    </div>
                  </div>
                </div>

                {printOrder.codAmount > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex justify-between items-center">
                      <span className="font-bold">TIỀN THU HỘ:</span>
                      <span className="text-lg font-bold">{printOrder.codAmount.toLocaleString()} VNĐ</span>
                    </div>
                  </div>
                )}

                <div className="mt-4 pt-4 border-t text-center">
                  <div className="text-4xl font-bold mb-2">||||| {printOrder.id} |||||</div>
                  <p className="text-xs">Mã vạch - {new Date().toLocaleDateString('vi-VN')}</p>
                </div>
              </div>
            )}
            
            <div className="flex gap-2">
              <Button 
                onClick={() => window.print()} 
                className="flex-1"
              >
                <Printer className="h-4 w-4 mr-2" />
                In tem
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowPrintLabel(false)}
                className="flex-1"
              >
                Đóng
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Orders;